import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemIcon,
  ListItemButton,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  People as PeopleIcon,
  SupervisorAccount as ManagerIcon,
  Person as EmployeeIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Business as DepartmentIcon,
  LocationOn as LocationIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import api from '../utils/api';

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'manager' | 'employee';
  department?: {
    id: number;
    name: string;
  };
  location?: {
    id: number;
    name: string;
  };
  is_active: boolean;
}

interface ManagementHierarchy {
  manager: User;
  managed_employees: User[];
  managed_departments: Array<{
    id: number;
    name: string;
    description: string;
  }>;
  managed_locations: Array<{
    id: number;
    name: string;
    address: string;
  }>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`manager-tabpanel-${index}`}
      aria-labelledby={`manager-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ManagerAssignment: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Data states
  const [hierarchy, setHierarchy] = useState<ManagementHierarchy[]>([]);
  const [availableManagers, setAvailableManagers] = useState<User[]>([]);
  const [availableEmployees, setAvailableEmployees] = useState<User[]>([]);
  
  // Dialog states
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState<User | null>(null);
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
  const [employeesToUnassign, setEmployeesToUnassign] = useState<number[]>([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [hierarchyRes, managersRes, employeesRes] = await Promise.all([
        api.get('/users/management_hierarchy/'),
        api.get('/users/available_managers/'),
        api.get('/users/available_employees/')
      ]);
      
      setHierarchy(hierarchyRes.data);
      setAvailableManagers(managersRes.data);
      setAvailableEmployees(employeesRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAssignEmployees = async () => {
    if (!selectedManager || selectedEmployees.length === 0) return;
    
    try {
      await api.post(`/users/${selectedManager.id}/assign_employees/`, {
        employee_ids: selectedEmployees
      });
      
      setSuccess(`Successfully assigned ${selectedEmployees.length} employees to ${selectedManager.first_name} ${selectedManager.last_name}`);
      setAssignDialogOpen(false);
      setSelectedManager(null);
      setSelectedEmployees([]);
      fetchData(); // Refresh data
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to assign employees');
    }
  };

  const handleUnassignEmployees = async () => {
    if (!selectedManager || employeesToUnassign.length === 0) return;
    
    try {
      await api.post(`/users/${selectedManager.id}/unassign_employees/`, {
        employee_ids: employeesToUnassign
      });
      
      setSuccess(`Successfully unassigned ${employeesToUnassign.length} employees from ${selectedManager.first_name} ${selectedManager.last_name}`);
      setUnassignDialogOpen(false);
      setSelectedManager(null);
      setEmployeesToUnassign([]);
      fetchData(); // Refresh data
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to unassign employees');
    }
  };

  const openAssignDialog = (manager: User) => {
    setSelectedManager(manager);
    setSelectedEmployees([]);
    setAssignDialogOpen(true);
  };

  const openUnassignDialog = (manager: User) => {
    setSelectedManager(manager);
    setEmployeesToUnassign([]);
    setUnassignDialogOpen(true);
  };

  const getManagerCard = (managerData: ManagementHierarchy) => {
    const { manager, managed_employees, managed_departments, managed_locations } = managerData;
    
    return (
      <Card key={manager.id} sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
              <ManagerIcon />
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6">
                {manager.first_name} {manager.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {manager.email}
              </Typography>
            </Box>
            <Chip 
              label={`${managed_employees.length} employees`}
              color="primary"
              variant="outlined"
            />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <DepartmentIcon sx={{ fontSize: 16, mr: 0.5 }} />
                Managed Departments
              </Typography>
              {managed_departments.length > 0 ? (
                managed_departments.map(dept => (
                  <Chip
                    key={dept.id}
                    label={dept.name}
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No departments assigned
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <LocationIcon sx={{ fontSize: 16, mr: 0.5 }} />
                Managed Locations
              </Typography>
              {managed_locations.length > 0 ? (
                managed_locations.map(loc => (
                  <Chip
                    key={loc.id}
                    label={loc.name}
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No locations assigned
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <EmployeeIcon sx={{ fontSize: 16, mr: 0.5 }} />
                Direct Employees
              </Typography>
              {managed_employees.length > 0 ? (
                <Box>
                  {managed_employees.slice(0, 3).map(emp => (
                    <Chip
                      key={emp.id}
                      label={`${emp.first_name} ${emp.last_name}`}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                  {managed_employees.length > 3 && (
                    <Chip
                      label={`+${managed_employees.length - 3} more`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No direct employees
                </Typography>
              )}
            </Grid>
          </Grid>
        </CardContent>
        
        <CardActions>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => openAssignDialog(manager)}
          >
            Assign Employees
          </Button>
          <Button
            size="small"
            startIcon={<RemoveIcon />}
            onClick={() => openUnassignDialog(manager)}
            disabled={managed_employees.length === 0}
          >
            Unassign Employees
          </Button>
        </CardActions>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Manager Assignment
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          variant="outlined"
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Management Hierarchy" />
            <Tab label="Available Managers" />
            <Tab label="Available Employees" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Current Management Structure
          </Typography>
          {hierarchy.length > 0 ? (
            hierarchy.map(getManagerCard)
          ) : (
            <Alert severity="info">
              No managers found. Create managers first to set up the management hierarchy.
            </Alert>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Available Managers ({availableManagers.length})
          </Typography>
          <List>
            {availableManagers.map(manager => (
              <ListItem key={manager.id} divider>
                <ListItemAvatar>
                  <Avatar>
                    <ManagerIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${manager.first_name} ${manager.last_name}`}
                  secondary={manager.email}
                />
                <Chip
                  label={manager.department?.name || 'No Department'}
                  size="small"
                  variant="outlined"
                />
              </ListItem>
            ))}
          </List>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Available Employees ({availableEmployees.length})
          </Typography>
          <List>
            {availableEmployees.map(employee => (
              <ListItem key={employee.id} divider>
                <ListItemAvatar>
                  <Avatar>
                    <EmployeeIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${employee.first_name} ${employee.last_name}`}
                  secondary={employee.email}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    label={employee.department?.name || 'No Department'}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={employee.location?.name || 'No Location'}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </ListItem>
            ))}
          </List>
        </TabPanel>
      </Paper>

      {/* Assign Employees Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign Employees to {selectedManager?.first_name} {selectedManager?.last_name}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Employees</InputLabel>
            <Select
              multiple
              value={selectedEmployees}
              onChange={(e) => setSelectedEmployees(e.target.value as number[])}
              input={<OutlinedInput label="Select Employees" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const employee = availableEmployees.find(emp => emp.id === value);
                    return (
                      <Chip key={value} label={`${employee?.first_name} ${employee?.last_name}`} />
                    );
                  })}
                </Box>
              )}
            >
              {availableEmployees.map((employee) => (
                <MenuItem key={employee.id} value={employee.id}>
                  <Checkbox checked={selectedEmployees.indexOf(employee.id) > -1} />
                  <ListItemText
                    primary={`${employee.first_name} ${employee.last_name}`}
                    secondary={employee.email}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAssignEmployees}
            variant="contained"
            disabled={selectedEmployees.length === 0}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unassign Employees Dialog */}
      <Dialog open={unassignDialogOpen} onClose={() => setUnassignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Unassign Employees from {selectedManager?.first_name} {selectedManager?.last_name}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Employees to Unassign</InputLabel>
            <Select
              multiple
              value={employeesToUnassign}
              onChange={(e) => setEmployeesToUnassign(e.target.value as number[])}
              input={<OutlinedInput label="Select Employees to Unassign" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const managerData = hierarchy.find(h => h.manager.id === selectedManager?.id);
                    const employee = managerData?.managed_employees.find(emp => emp.id === value);
                    return (
                      <Chip key={value} label={`${employee?.first_name} ${employee?.last_name}`} />
                    );
                  })}
                </Box>
              )}
            >
              {hierarchy
                .find(h => h.manager.id === selectedManager?.id)
                ?.managed_employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    <Checkbox checked={employeesToUnassign.indexOf(employee.id) > -1} />
                    <ListItemText
                      primary={`${employee.first_name} ${employee.last_name}`}
                      secondary={employee.email}
                    />
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnassignDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUnassignEmployees}
            variant="contained"
            color="warning"
            disabled={employeesToUnassign.length === 0}
          >
            Unassign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManagerAssignment; 